import type { LucideIcon } from 'lucide-react';
import { Activity, ClipboardList, Landmark, ShieldCheck, Smartphone, Workflow, Layers, Zap, LayoutGrid, UserCheck, Network, Building2, Bot, LayoutDashboard, GitMerge, Car, Globe, Wallet } from 'lucide-react';

export interface Product {
    id: string;
    name: string;
    description: string;
    slug: string;
    icon: LucideIcon;
    theme: string;
    type: 'web' | 'mobile';
}

export const products: Product[] = [
    {
        id: '100',
        name: 'XpressLinc',
        description: 'Strategic end-to-end fintech journey orchestrating AePS, UPI Issuing, Merchant Acquiring, and Real-time Payments.',
        slug: 'unified-story',
        icon: Workflow,
        theme: 'Blue',
        type: 'web',
    },
    {
        id: '1',
        name: 'Merchant Onboarding',
        description: 'A seamless digital onboarding experience for retail and corporate banking clients.',
        slug: 'merchant-onboarding',
        icon: Smartphone,
        theme: 'Blue',
        type: 'mobile',
    },
    {
        id: '17',
        name: 'CBS Mobile Application',
        description: 'White-label core banking mobile app — dynamic bank identity, onboarding, MPIN login, transfers, statements, and a service hub.',
        slug: 'cbs-mobile',
        icon: Wallet,
        theme: 'Blue',
        type: 'mobile',
    },
    {
        id: '2',
        name: 'Banking App',
        description: 'Premium mobile banking interface featuring high-fidelity dashboard, quick actions, and real-time transaction tracking.',
        slug: 'banking-app',
        icon: Landmark,
        theme: 'Blue',
        type: 'mobile',
    },
    {
        id: '14',
        name: 'UPI Switch Demo',
        description: 'Complete UPI ecosystem simulator (Acquiring, Issuing, Credit Line) with real-time protocol visualization and lifecycle management.',
        slug: 'upi-switch-demo',
        icon: LayoutGrid,
        theme: 'Blue',
        type: 'web',
    },
    {
        id: '11',
        name: 'BBPS Digital Ecosystem',
        description: 'Comprehensive Bharat Bill Payment System suite including Mobile App, Backend Simulator, Dashboard, Reports, and NPCI Reconciliation.',
        slug: 'bbps-ecosystem',
        icon: Layers,
        theme: 'Indigo',
        type: 'web',
    },
    {
        id: '16',
        name: 'BharatLinc',
        description: 'Unified NBBL Bharat Connect platform — 1. BOU + Biller (Lifecycle, Maker-Checker, Settlement, 3-Way RCON, Revenue, Disputes, Compliance) & 2. COU + AI (Agent Institutions, Bill Fetch & Pay).',
        slug: 'bharat-connect-ecosystem',
        icon: Network,
        theme: 'Violet',
        type: 'web',
    },
    {
        id: '12',
        name: 'BANL Switch Demo',
        description: 'Interactive Beneficiary Account Name Lookup simulator showing real-time banking switch flow (LincPay → NPCI → CBS).',
        slug: 'banl-demo',
        icon: Zap,
        theme: 'Emerald',
        type: 'web',
    },
    {
        id: '13',
        name: 'IMPS Switch Demo',
        description: 'Advanced interbank fund transfer engine (24x7) with real-time settlement, auto-refunds, and NPCI routing visualization.',
        slug: 'imps-demo',
        icon: Activity,
        theme: 'Blue',
        type: 'web',
    },
    {
        id: '4',
        name: 'Bank Onboarding',
        description: 'Automated digital transformation for traditional banking institutions with seamless data migration.',
        slug: 'bank-demo',
        icon: Landmark,
        theme: 'Indigo',
        type: 'web',
    },
    {
        id: '5',
        name: 'CBS Onboarding',
        description: 'Core Banking System integration layer with robust technical setup and multi-IP whitelisting.',
        slug: 'cbs-demo',
        icon: ShieldCheck,
        theme: 'Cyan',
        type: 'web',
    },
    {
        id: '15',
        name: 'AePS Switch Demo',
        description: 'Secure Aadhaar-based identity and payment system (AePS Switch) featuring Web Redirection, SDK flows, and real-time security audits.',
        slug: 'aeps-switch-demo',
        icon: UserCheck,
        theme: 'Blue',
        type: 'web',
    },
    {
        id: '18',
        name: 'LOS, LMS & Digital Lending',
        description: 'End-to-end digital lending suite for Banks & NBFCs: 6-step guided origination, DigiLocker eKYC, Account Aggregator underwriting, BRE engine, Maker-Checker approvals, LMS servicing, and DPD Collections Kanban.',
        slug: 'los-lms',
        icon: Landmark,
        theme: 'Blue',
        type: 'web',
    },
];

// BBPS SUB-MODULES
export const bbpsSubModules = [
    {
        name: 'BBPS Mobile',
        description: 'Standardized Bharat Bill Payment System customer interface.',
        slug: 'bbps-mobile',
        icon: ShieldCheck,
        theme: 'Green',
        type: 'mobile',
    },
    {
        name: 'BBPS Backend',
        description: 'Backend orchestration and state-driven pipeline visualizer.',
        slug: 'bbps-backend',
        icon: ShieldCheck,
        theme: 'Emerald',
        type: 'web',
    },
    {
        name: 'BBPS Architecture',
        description: 'Distributed system architecture and real-time telemetry.',
        slug: 'bbps-architecture',
        icon: Workflow,
        theme: 'Blue',
        type: 'web',
    },
    {
        name: 'BBPS Reconciliation',
        description: 'Institutional NPCI settlement cycle and reversal engine.',
        slug: 'bbps-reconciliation',
        icon: Workflow,
        theme: 'Orange',
        type: 'web',
    },
    {
        name: 'BBPS Dashboard',
        description: 'Live operational monitoring for COU and BOU units.',
        slug: 'bbps-dashboard',
        icon: Activity,
        theme: 'Slate',
        type: 'web',
    },
    {
        name: 'BBPS Reports',
        description: 'Comprehensive audit and settlement reporting hub.',
        slug: 'bbps-reports',
        icon: ClipboardList,
        theme: 'Slate',
        type: 'web',
    },
    {
        name: 'COU Dashboard',
        description: 'Customer Operating Unit — real-time transaction monitoring and biller performance.',
        slug: 'bbps-cou',
        icon: LayoutGrid,
        theme: 'Blue',
        type: 'web',
    },
    {
        name: 'BOU Dashboard',
        description: 'Biller Operating Unit — settlement tracking and inbound transaction management.',
        slug: 'bbps-bou',
        icon: Landmark,
        theme: 'Emerald',
        type: 'web',
    },
];

// BHARAT CONNECT SUB-MODULES (8 modules, 1 per PDF)
export const bharatConnectSubModules = [
    {
        name: 'BharatLinc Hub',
        description: 'Unified BharatLinc Command Center — 1. BOU + Biller & 2. COU + AI.',
        slug: 'bharat-connect',
        icon: Globe,
        theme: 'Violet',
        type: 'web',
    },
    {
        name: 'Biller Lifecycle',
        description: 'BOU biller onboarding 6-step pipeline, modification, termination with Maker-Checker.',
        slug: 'bharat-connect-biller-lifecycle',
        icon: Building2,
        theme: 'Blue',
        type: 'web',
    },
    {
        name: 'AI Agent Lifecycle',
        description: 'Agent Institution onboarding, modification, termination with Maker-Checker governance.',
        slug: 'bharat-connect-ai-lifecycle',
        icon: Bot,
        theme: 'Purple',
        type: 'web',
    },
    {
        name: 'BOU Dashboard & Reports',
        description: 'Biller Operating Unit — live metrics, settlement cycle tracking, MIS reporting.',
        slug: 'bharat-connect-bou-dashboard',
        icon: LayoutDashboard,
        theme: 'Emerald',
        type: 'web',
    },
    {
        name: 'Settlement Flow',
        description: '8 intra-day multilateral net clearing cycles — NBBL, NPCI/RBI settlement, T+1 biller crediting.',
        slug: 'bharat-connect-settlement-flow',
        icon: Landmark,
        theme: 'Emerald',
        type: 'web',
    },
    {
        name: 'RCON Dashboard',
        description: '3-way reconciliation confirmation — matched/exceptions/EOD recon, 99.85% accuracy.',
        slug: 'bharat-connect-rcon',
        icon: GitMerge,
        theme: 'Orange',
        type: 'web',
    },
    {
        name: 'Architecture',
        description: 'LincPay Bharat Connect technical architecture, container layout, and ER diagram.',
        slug: 'bharat-connect-architecture',
        icon: Layers,
        theme: 'Slate',
        type: 'web',
    },
    {
        name: 'Biller Portal (MTD)',
        description: 'Maharashtra Traffic Dept. eChallan biller self-service portal — live dashboard, analytics, settlement.',
        slug: 'bharat-connect-biller-portal',
        icon: Car,
        theme: 'Rose',
        type: 'web',
    },
];
